import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { RefreshCw, CheckCircle2, User, Building, XCircle, Facebook, Instagram, ExternalLink, AlertTriangle, BookOpen, Copy, Rss, TestTube, Check, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { supabase } from '@/lib/supabase';
import { useBrandPermissions } from '@/lib/useBrandRole';
// import {
//   DndContext,
//   closestCenter,
//   KeyboardSensor,
//   PointerSensor,
//   useSensor,
//   useSensors,
//   DragEndEvent,
// } from '@dnd-kit/core';
// import {
//   arrayMove,
//   SortableContext,
//   sortableKeyboardCoordinates,
//   verticalListSortingStrategy,
//   useSortable,
// } from '@dnd-kit/sortable';
// import {
//   CSS,
// } from '@dnd-kit/utilities';
import { useTimezone } from '@/hooks/useTimezone';
// import { Clock } from 'lucide-react';

// Platform types from BrandSettings
interface Platform {
  id: string;
  name: string;
  platform_key: string;
  type: 'SOCIAL' | 'BLOG';
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  enabled: boolean;
  description?: string;
}

interface PlatformBrandSetting {
  id: string;
  brand_id: string;
  platform_id: string;
  autopost_enabled: boolean;
  autogenerate_enabled?: boolean;
  posting_frequency: string;
  preferred_time: string;
  preferred_days: string[] | null;
  metadata: Record<string, any> | null;
  config?: {
    image?: {
      enabled: boolean;
      steps: string[];
    };
    video?: {
      enabled: boolean;
      steps: string[];
    };
  };
  created_at: string;
  updated_at: string;
  status: string;
  auth_token?: string | null;
  auth_refresh_token?: string | null;
  auth_expires_at?: string | null;
  connection_details: Record<string, any> | null;
  error_message?: string | null;
  user_id?: string;
}

// Add the LinkedInOrganization interface
interface LinkedInOrganization {
  id: string;
  name: string;
  vanityName?: string;
  logoUrl?: string;
}

interface PlatformConnectionCardProps {
  platform: Platform | undefined;
  icon: React.ReactNode;
  platformSettings: Record<string, PlatformBrandSetting>;
  platformConnectionStatus: Record<string, 'idle' | 'success' | 'error' | 'testing'>;
  savePlatformSettings: (platformId: string, settings: any) => Promise<void>;
  testPlatformConnection: (platformId: string) => Promise<void>;
  updateConnectionStatus: (platformId: string, status: 'CONNECTED' | 'DISCONNECTED' | 'AUTHENTICATION_FAILED' | 'PENDING', errorMessage?: string) => Promise<void>;
  initiateAuthFlow?: (platformKey: string, authType?: 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN') => Promise<void>;
  // Fix the return type to allow for undefined
  fetchLinkedInOrganizations?: (platformId: string) => Promise<any>;
  setLinkedInOrganization?: (platformId: string, organizationId: string) => Promise<boolean | undefined>;
  linkedinOrganizations?: LinkedInOrganization[];
  loadingOrganizations?: boolean;
  // Facebook page management
  setFacebookPage?: (platformId: string, pageId: string) => Promise<boolean | undefined>;
  // Brand ID for cost calculator
  brandId: string;
}

// Add constants for all available steps
// const ALL_IMAGE_STEPS = ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"];
// const ALL_VIDEO_STEPS = ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"];

// Add StepCard component (no DnD for now)
/* const StepCard: React.FC<{
  id: string;
  step: string;
  enabled: boolean;
  onToggle: (step: string, enabled: boolean) => void;
  disabled?: boolean;
  type: 'image' | 'video';
}> = ({ step, enabled, onToggle, disabled = false, type }) => {
  // DnD disabled in this card to avoid unused imports causing build errors
  const style = {} as React.CSSProperties;

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'SEARCH_EXISTING':
        return <Search className="h-4 w-4" />;
      case 'AI_GENERATE':
        return <Bot className="h-4 w-4" />;
      case 'STOCK_SEARCH':
        return <Eye className="h-4 w-4" />;
      case 'STOP_AND_UPLOAD':
        return <Upload className="h-4 w-4" />;
      default:
        return <div className="h-4 w-4 rounded bg-gray-300" />;
    }
  };

  const getStepLabel = (step: string) => {
    return step.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStepDescription = (step: string, type: 'image' | 'video') => {
    const costs = {
      image: {
        SEARCH_EXISTING: 1,
        STOCK_SEARCH: 10,
        AI_GENERATE: 100,
        STOP_AND_UPLOAD: 0
      },
      video: {
        SEARCH_EXISTING: 1,
        STOCK_SEARCH: 100,
        AI_GENERATE: 900,
        STOP_AND_UPLOAD: 0
      }
    };

    switch (step) {
      case 'SEARCH_EXISTING':
        return (
          <div className="flex flex-col">
            <span>Search through existing brand assets</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'AI_GENERATE':
        return (
          <div className="flex flex-col">
            <span>Generate new content using AI</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'STOCK_SEARCH':
        return (
          <div className="flex flex-col">
            <span>Search stock photo/video libraries</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'STOP_AND_UPLOAD':
        return (
          <div className="flex flex-col">
            <span>Stop and prompt user to upload manually</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      default:
        return '';
    }
  };

  return null;
}; */

// RSS-specific display component
const RssConnectionDisplay: React.FC<{
  platform: Platform;
  brandId: string;
}> = ({ brandId }) => {
  const navigate = useNavigate();
  // const { isLoading } = useTimezone();
  const workerUrl = import.meta.env.VITE_WORKERS_URL;
  const feedUrl = `${workerUrl}/blog-rss/${brandId}`;

  // Helper function to format current time (not used in this component)

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "RSS feed URL copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy URL",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* RSS Feed URL Display */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-medium mb-2">Your RSS Feed URL</h3>
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <code className="flex-1 text-sm">{feedUrl}</code>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(feedUrl)}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(feedUrl, '_blank')}>
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-medium mb-2">How to use your RSS feed</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Your RSS feed automatically includes all published blog posts from your brand.</p>
            <p>Share the feed URL above with your audience so they can:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Subscribe to your content in RSS readers like Feedly, Inoreader, or NewsBlur</li>
              <li>Add your feed to their browser's RSS extensions</li>
              <li>Import your feed into their preferred news aggregator</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* RSS Feed Preview Link */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Preview Your Feed</h3>
              <p className="text-sm text-muted-foreground">View your RSS feed content and recent posts</p>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate(`/brands/${brandId}/rss-feed`)}
            >
              <Rss className="mr-2 h-4 w-4" />
              View RSS Feed
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

const PlatformConnectionCard: React.FC<PlatformConnectionCardProps> = ({
  platform,
  icon,
  platformSettings,
  platformConnectionStatus,
  savePlatformSettings,
  testPlatformConnection,
  updateConnectionStatus,
  initiateAuthFlow,
  // Add new props
  fetchLinkedInOrganizations,
  setLinkedInOrganization,
  linkedinOrganizations = [],
  loadingOrganizations = false,
  // Facebook page management
  setFacebookPage,
  // Brand ID for cost calculator
  brandId
}) => {
  if (!platform) return null;

  // Get brand permissions to control button visibility
  const { canViewBrand } = useBrandPermissions(brandId);

  // Navigation hook for redirecting after save
  const navigate = useNavigate();
  
  const { timezone, isLoading } = useTimezone();
  
  const settings = platformSettings[platform.id] || {
    autopost_enabled: false,
    autogenerate_enabled: false,
    posting_frequency: 'DAILY',
    preferred_time: '09:00',
    preferred_days: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    status: 'DISCONNECTED',
    connection_details: null
  };

  // Convert UTC preferred time to user's timezone for display
  const displayPreferredTime = useMemo(() => {
    if (!settings.preferred_time) return '09:00';
    
    // Don't perform timezone conversion while timezone is still loading
    if (isLoading) {
      console.log('=== TIMEZONE DEBUG: LOADING STATE ===');
      console.log('Timezone is loading, showing raw preferred_time:', settings.preferred_time);
      console.log('isLoading:', isLoading);
      console.log('Current timezone (default):', timezone);
      console.log('=== END LOADING STATE DEBUG ===');
      return settings.preferred_time; // Return the raw time while loading
    }
    
    console.log('=== TIMEZONE DEBUG START ===');
    console.log('Raw settings.preferred_time from database:', settings.preferred_time);
    console.log('User timezone from useTimezone hook:', timezone);
    console.log('Browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
    console.log('isLoading:', isLoading);
    
    try {
      // Parse the stored UTC time (HH:MM format) 
      const [utcHours, utcMinutes] = settings.preferred_time.split(':').map(Number);
      console.log('Parsed UTC hours:', utcHours, 'minutes:', utcMinutes);
      
      // Create a proper UTC date object for today at the stored UTC time
      const today = new Date();
      console.log('Today date:', today.toString());
      
      const utcDate = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        utcHours,
        utcMinutes,
        0,
        0
      ));
      console.log('Created UTC date:', utcDate.toISOString());
      console.log('UTC date toString():', utcDate.toString());
      
      // Test what this date looks like in different formats
      console.log('utcDate.getHours() (local):', utcDate.getHours());
      console.log('utcDate.getUTCHours() (UTC):', utcDate.getUTCHours());
      
      // The key conversion step
      const localTime = utcDate.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
      console.log('Converted local time result:', localTime);
      console.log('Timezone used for conversion:', timezone);
      
      // Also test with a few other methods to see what works
      const testTime1 = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(utcDate);
      console.log('Alternative method 1:', testTime1);
      
      const testTime2 = utcDate.toLocaleString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      console.log('Alternative method 2:', testTime2);
      
      console.log('=== TIMEZONE DEBUG END ===');
      
      return localTime;
    } catch (error) {
      console.error('Error converting display time:', error);
      return '09:00';
    }
  }, [settings.preferred_time, timezone, isLoading]);

  const [formState, setFormState] = useState({
    autopost_enabled: settings.autopost_enabled || false,
    autogenerate_enabled: settings.autogenerate_enabled || false,
    posting_frequency: settings.posting_frequency || 'WEEKLY',
    preferred_time: displayPreferredTime,
    preferred_days: settings.preferred_days || ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    config: settings.config || {
      image: {
        enabled: true,
        steps: ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"]
      },
      video: {
        enabled: false,
        steps: ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"]
      }
    }
  });

  // Sync formState.preferred_time with displayPreferredTime when it changes
  useEffect(() => {
    console.log('[PlatformConnectionCard] Syncing formState with displayPreferredTime:', {
      displayPreferredTime,
      currentFormStateTime: formState.preferred_time,
      shouldUpdate: displayPreferredTime !== formState.preferred_time
    });
    
    setFormState(prev => ({
      ...prev,
      preferred_time: displayPreferredTime
    }));
  }, [displayPreferredTime]);

  // Sync formState.preferred_days with settings when they change (but not when user makes changes)
  useEffect(() => {
    const settingsPreferredDays = settings.preferred_days || ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    console.log('[PlatformConnectionCard] Settings preferred_days changed:', {
      settingsPreferredDays,
      currentFormStatePreferredDays: formState.preferred_days
    });
    
    setFormState(prev => ({
      ...prev,
      preferred_days: settingsPreferredDays
    }));
  }, [settings.preferred_days]);

  // Add state to track the current order of all steps
  // Step ordering state (currently unused)
  // const [imageStepsOrder, setImageStepsOrder] = useState(ALL_IMAGE_STEPS);
  // const [videoStepsOrder, setVideoStepsOrder] = useState(ALL_VIDEO_STEPS);
  const [testLoading, setTestLoading] = useState(false);

  // Configure drag and drop sensors
  // DnD sensors disabled for now

  // State for Instagram auth type
  const [instagramAuthType, setInstagramAuthType] = useState<'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN'>(
    settings.connection_details?.auth_type || 'FACEBOOK_LOGIN'
  );

  // State for Instagram connection modal
  const [showInstagramModal, setShowInstagramModal] = useState(false);

  // State for Facebook connection modal
  const [showFacebookModal, setShowFacebookModal] = useState(false);

  // Fetch LinkedIn organizations if platform is LinkedIn and is connected
  useEffect(() => {
    const isLinkedIn = platform.platform_key === 'linkedin';
    const isConnected = settings.status === 'CONNECTED';
    
    if (isLinkedIn && isConnected && fetchLinkedInOrganizations) {
      // Add a ref to track if the component is mounted
      const isMounted = true;
      
      // We'll use a flag to only fetch once per component mount
      const hasAttemptedFetch = localStorage.getItem(`org_fetch_attempted_${platform.id}`);
      
      if (!hasAttemptedFetch && linkedinOrganizations.length === 0) {
        localStorage.setItem(`org_fetch_attempted_${platform.id}`, 'true');
        fetchLinkedInOrganizations(platform.id)
          .then(() => {
            // Clear the flag after 5 minutes to allow future fetches
            setTimeout(() => {
              if (isMounted) {
                localStorage.removeItem(`org_fetch_attempted_${platform.id}`);
              }
            }, 300000); // 5 minutes
          });
      }
      
      // Cleanup function
      return () => {
        // This indicates the component is unmounting
        localStorage.removeItem(`org_fetch_attempted_${platform.id}`);
      };
    }
  }, [platform.platform_key, platform.id, settings.status, fetchLinkedInOrganizations, linkedinOrganizations.length]);

  // Extract user profile information from connection details
  const connectionDetails = settings.connection_details || {};
  const userProfileInfo = getUserProfileInfo(platform.platform_key, connectionDetails);

  // Get currently selected organization ID from connection details (LinkedIn)
  const selectedOrganizationId = connectionDetails.organization_id;
  const isPostingAsOrganization = connectionDetails.post_as_organization === true;

  // Get Facebook page information
  const selectedFacebookPageId = connectionDetails.selected_page_id;
  const isPostingAsFacebookPage = !!selectedFacebookPageId;
  const facebookPages = connectionDetails.pages || [];
  const selectedFacebookPage = facebookPages.find((page: any) => page.id === selectedFacebookPageId);

  // DEBUG: Log Facebook page data for debugging
  useEffect(() => {
    if (platform.platform_key === 'facebook') {
      console.log('=== FACEBOOK PAGE DEBUG ===');
      console.log('Platform ID:', platform.id);
      console.log('Settings object:', settings);
      console.log('Connection Details:', connectionDetails);
      console.log('Selected Page ID:', selectedFacebookPageId);
      console.log('Is Posting As Page:', isPostingAsFacebookPage);
      console.log('Facebook Pages:', facebookPages);
      console.log('Selected Page Object:', selectedFacebookPage);
      console.log('User Profile Info from getUserProfileInfo:', userProfileInfo);
      console.log('=== END FACEBOOK PAGE DEBUG ===');
    }
  }, [platform.platform_key, platform.id, settings, connectionDetails, selectedFacebookPageId, isPostingAsFacebookPage, facebookPages, selectedFacebookPage, userProfileInfo]);

  // Get the selected organization name for display
  const selectedOrganization = linkedinOrganizations.find(org => org.id === selectedOrganizationId);

  function getUserProfileInfo(platformKey: string, details: Record<string, any>) {
    if (!details) return null;
    
    switch (platformKey) {
      case 'linkedin':
        // For LinkedIn, we might have profile info stored
        if (details.name) return details.name;
        if (details.localizedFirstName && details.localizedLastName) {
          return `${details.localizedFirstName} ${details.localizedLastName}`;
        }
        if (details.username) return details.username;
        return null;
      
      case 'twitter':
        // For X (formerly Twitter), we typically have username and name
        if (details.username) return `@${details.username}`;
        if (details.name) return details.name;
        return null;
      
      case 'facebook':
        // For Facebook, prioritize selected page name over user profile
        if (details.selected_page_name) return details.selected_page_name;
        // Fall back to user profile information
        if (details.user_name) return details.user_name;
        if (details.name) return details.name;
        if (details.username) return details.username;
        return null;
      
      case 'instagram':
        // For Instagram, we typically have instagram_username or username
        if (details.instagram_username) return `@${details.instagram_username}`;
        if (details.username) return `@${details.username}`;
        if (details.name) return details.name;
        return null;
      
      default:
        // For other platforms, try common fields
        if (details.username) return details.username;
        if (details.name) return details.name;
        if (details.display_name) return details.display_name;
        if (details.email) return details.email;
        return null;
    }
  }


  const handleSave = async () => {
    try {
      // Save only connection-related settings (auto-publish toggle and connection details)
      // Automation settings like auto-create, frequency, time, and media config are handled in the Automation tab
      await savePlatformSettings(platform.id, {
        autopost_enabled: formState.autopost_enabled,
        // Keep existing automation and media settings
        autogenerate_enabled: settings.autogenerate_enabled,
        posting_frequency: settings.posting_frequency,
        preferred_time: settings.preferred_time,
        preferred_days: settings.preferred_days,
        config: settings.config
      });

      // Navigate to automation page after successful save
      navigate('/automation');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      // Don't navigate if there's an error
    }
  };

  const isConnected = settings.status === 'CONNECTED';
  const hasFailed = settings.status === 'AUTHENTICATION_FAILED';
  const isPending = settings.status === 'PENDING';
  const connectionStatus = platformConnectionStatus[platform.id] || 'idle';
  
  // Function to handle disconnecting a platform
  const handleDisconnect = () => {
    updateConnectionStatus(platform.id, 'DISCONNECTED');
  };
  
  // Function to handle connecting to a platform
  const handleConnect = () => {
    if (isConnected) {
      // For existing connections, test the connection
      testPlatformConnection(platform.id);
    } else {
      // For new connections, initiate the auth flow
      if (initiateAuthFlow) {
        // If platform is Instagram, only show the modal
        if (platform.platform_key === 'instagram') {
          setShowInstagramModal(true);
          return;
        } else if (platform.platform_key === 'facebook') {
          setShowFacebookModal(true);
        } else {
          initiateAuthFlow(platform.platform_key);
        }
      } else {
        toast({
          title: "Authentication Error",
          description: "Authentication flow not available for this platform.",
          variant: "destructive"
        });
      }
    }
  };

  // Function to handle Instagram authentication after modal confirmation
  const handleInstagramAuth = () => {
    setShowInstagramModal(false);
    if (initiateAuthFlow) {
      initiateAuthFlow(platform.platform_key, instagramAuthType);
    }
  };

  // Function to handle Facebook authentication after modal confirmation
  const handleFacebookAuth = () => {
    setShowFacebookModal(false);
    if (initiateAuthFlow) {
      initiateAuthFlow(platform.platform_key);
    }
  };

  // Function to cancel a pending authentication
  const handleCancelAuth = () => {
    updateConnectionStatus(platform.id, 'DISCONNECTED', 'Authentication canceled by user');
    toast({
      title: "Authentication Canceled",
      description: `${platform.name} authentication has been canceled.`,
    });
  };

  // Add handler for organization selection
  const handleOrganizationChange = async (organizationId: string) => {
    // Both personal and organization cases are handled by the setLinkedInOrganization function
    if (setLinkedInOrganization) {
      await setLinkedInOrganization(platform.id, organizationId);
    }
  };

  // Add handler for Facebook page selection
  const handleFacebookPageChange = async (pageId: string) => {
    if (setFacebookPage) {
      await setFacebookPage(platform.id, pageId);
    }
  };

  // Add drag end handler for image steps
  // DnD handlers disabled
  // const handleImageDragEnd = () => {};

  // Add drag end handler for video steps
  // const handleVideoDragEnd = () => {};

  // Step toggle handlers not used in this view currently

  // Helper function to format last updated timestamp in a user-friendly way
  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Updated just now';
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `Updated ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInHours < 168) { // Less than a week
      const days = Math.floor(diffInHours / 24);
      return `Updated ${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      return `Updated on ${date.toLocaleDateString()}`;
    }
  };

  // formatCurrentTime helper removed (unused)

  // Handle test content generation
  const handleTestContent = async () => {
    setTestLoading(true);
    
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No session found');
      }
      
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/test-platform-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brandId: brandId,
          platformId: platform.id,
          markConvergenceAsUsed: false
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate test content');
      }
      
      // Show success toast with appropriate message and link to content
      if (data.scheduledTime) {
        toast({
          title: "Test Content Created",
          description: (
            <div className="space-y-2">
              <p>Content will be published to {platform.name} in 2 minutes</p>
              {data.contentId && (
                <a 
                  href={`/content/${data.contentId}`}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  View Content →
                </a>
              )}
            </div>
          ),
          duration: Infinity, // Keep toast visible until manually dismissed
        });
      } else {
        toast({
          title: "Test Content Created",
          description: (
            <div className="space-y-2">
              <p>Content created for {platform.name}. Manual publishing required.</p>
              {data.contentId && (
                <a 
                  href={`/content/${data.contentId}`}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  View Content →
                </a>
              )}
            </div>
          ),
          duration: Infinity, // Keep toast visible until manually dismissed
        });
      }
    } catch (error) {
      console.error('Error generating test content:', error);
      toast({
        title: "Test Failed",
        description: error instanceof Error ? error.message : 'Failed to generate test content',
        variant: "destructive"
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    
    <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            {/* Facebook Connection Modal */}
            <Dialog open={showFacebookModal} onOpenChange={setShowFacebookModal}>
              <DialogPortal>
              <DialogOverlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
              <DialogContent className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full sm:max-w-lg overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-white border border-blue-200/50 shadow-2xl backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-indigo-50/20" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-12 translate-x-12" />
          
          <DialogHeader className="relative text-center pb-4">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 shadow-lg shadow-blue-200/50 transition-transform duration-300 hover:scale-110">
              <Facebook className="h-6 w-6 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
              Connect Facebook Business Account
            </DialogTitle>
          </DialogHeader>

          <Alert className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/60 shadow-lg backdrop-blur-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-50/30 to-yellow-50/30" />
            <AlertTriangle className="relative h-5 w-5 text-amber-600" />
            <AlertDescription className="relative text-amber-800">
              <div className="font-bold mb-2 text-amber-900 text-base">Important note</div>
              <p className="text-sm leading-relaxed">
                To setup Meta to Enable Facebook Business Scheduling, make sure:
              </p>
              <ul className="text-sm mt-1 space-y-1 list-disc list-inside">
                <li>You are the admin of a Facebook Business Page.</li>
                <li>Your Facebook Page is part of a Business Portfolio.</li>
                <li>Your personal Facebook account has admin rights to the Business Portfolio that the Page is a part of.</li>
              </ul>
              <p className="text-sm mt-2">
                If you are not sure if you have the right setup, review our guide:
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-1 h-8 text-xs"
                onClick={() => {
                  // Navigate to help page and show Facebook guide
                  window.open('/help#facebook-guide', '_blank');
                }}
              >
                <BookOpen className="mr-1 h-3 w-3" />
                See Connection Guide
              </Button>
            </AlertDescription>
          </Alert>

          <div className="text-center text-sm text-muted-foreground py-2">
            We're sending you to Facebook (Meta) real quick to sign in and link 
            your account to Rainfactory.
          </div>

          <div className="flex justify-between gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowFacebookModal(false)}
              className="group relative overflow-hidden flex-1 border-gray-300 text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md font-medium"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative">Back</span>
            </Button>
            <Button 
              onClick={handleFacebookAuth}
              className="group relative overflow-hidden flex-1 bg-gradient-to-r from-blue-600 to-blue-700 border-0 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-700 hover:to-blue-800 font-medium"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative flex items-center">
                Go to Facebook Business
                <ExternalLink className="ml-2 h-4 w-4" />
              </span>
            </Button>
          </div>
        </DialogContent>
        </DialogPortal>
      </Dialog>
      {/* Add decorative background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
      
      {/* Header Section */}
      <div className="relative mb-6">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="p-2 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 rounded-lg">
            {icon}
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">{platform?.name} Connection</h2>
            <p className="text-sm text-gray-600">Configure platform connection and automation settings</p>
          </div>
        </div>
      </div>
      
      <Card className={cn(
        "relative mb-4 border-none rounded-t-none pt-0 bg-white/60 backdrop-blur-sm ",
        // Remove error styling for RSS since it doesn't have connection states
        platform?.platform_key !== 'rss' && hasFailed && "border-l-4 border-red-500",
        platform?.platform_key !== 'rss' && isPending && "border-l-4 border-yellow-500"
      )}>
        <CardContent className="space-y-6 pt-6">
          {/* Render RSS-specific UI or regular platform UI */}
          {platform?.platform_key === 'rss' ? (
            <>
              <RssConnectionDisplay platform={platform} brandId={brandId} />
              <div className="flex justify-end mt-4 gap-2">
                {canViewBrand && isConnected && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleTestContent}
                          disabled={testLoading || !formState.autogenerate_enabled}
                          className="border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testLoading ? (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <TestTube className="mr-2 h-4 w-4" />
                              Test Auto-Create
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      {!formState.autogenerate_enabled ? (
                        <TooltipContent>
                          <p>Auto-create must be enabled to test content generation</p>
                        </TooltipContent>
                      ) : formState.autopost_enabled ? (
                        <TooltipContent>
                          <p>This will create test content and automatically publish it to RSS feed</p>
                        </TooltipContent>
                      ) : (
                        <TooltipContent>
                          <p>This will create test content (manual publishing required)</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
                <Button 
                  type="button"
                  onClick={handleSave}
                  className="group relative overflow-hidden h-12 px-6 min-w-[200px] bg-gradient-to-r from-blue-500 to-indigo-600 border-0 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-600 hover:to-indigo-700 font-medium"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative">Save RSS Settings</span>
                </Button>
              </div>
            </>
          ) : (
            /* Regular platform connection UI */
            <div className="space-y-6">
              {/* Enhanced Connection Status Header */}
              <Card className={cn(
                "group relative overflow-hidden border-l-4 transition-all duration-500",
                isConnected && "border-green-500 bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-white",
                hasFailed && "border-red-500 bg-gradient-to-br from-red-50/80 via-rose-50/60 to-white", 
                isPending && "border-yellow-500 bg-gradient-to-br from-amber-50/80 via-yellow-50/60 to-white",
                !isConnected && !hasFailed && !isPending && "border-gray-300 bg-gradient-to-br from-gray-50/80 via-slate-50/60 to-white"
              )}>
                <div className={cn(
                  "absolute inset-0 transition-opacity duration-300",
                  isConnected && "bg-gradient-to-br from-emerald-50/30 via-transparent to-green-50/20",
                  hasFailed && "bg-gradient-to-br from-red-50/30 via-transparent to-rose-50/20",
                  isPending && "bg-gradient-to-br from-amber-50/30 via-transparent to-yellow-50/20",
                  !isConnected && !hasFailed && !isPending && "bg-gradient-to-br from-gray-50/30 via-transparent to-slate-50/20"
                )} />
                {/* Decorative gradient orbs */}
                <div className={cn(
                  "absolute top-0 right-0 w-24 h-24 rounded-full -translate-y-12 translate-x-12 transition-colors duration-300",
                  isConnected && "bg-gradient-to-br from-emerald-200/30 to-transparent",
                  hasFailed && "bg-gradient-to-br from-red-200/30 to-transparent",
                  isPending && "bg-gradient-to-br from-amber-200/30 to-transparent",
                  !isConnected && !hasFailed && !isPending && "bg-gradient-to-br from-gray-200/30 to-transparent"
                )} />
                <CardContent className="relative p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "flex items-center justify-center w-10 h-10 rounded-full shadow-sm transition-all duration-300 group-hover:scale-110",
                        isConnected && "bg-gradient-to-br from-emerald-100 to-green-200 text-emerald-700",
                        hasFailed && "bg-gradient-to-br from-red-100 to-rose-200 text-red-700",
                        isPending && "bg-gradient-to-br from-amber-100 to-yellow-200 text-amber-700",
                        !isConnected && !hasFailed && !isPending && "bg-gradient-to-br from-gray-100 to-slate-200 text-gray-700"
                      )}>
                        {isPending ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : hasFailed ? (
                          <AlertCircle className="h-5 w-5" />
                        ) : isConnected ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <XCircle className="h-5 w-5" />
                        )}
                      </div>
                      
                      <div>
                        <div className={cn(
                          "font-bold text-lg transition-all duration-300",
                          isConnected && "bg-gradient-to-r from-emerald-800 to-green-700 bg-clip-text text-transparent",
                          hasFailed && "bg-gradient-to-r from-red-800 to-rose-700 bg-clip-text text-transparent",
                          isPending && "bg-gradient-to-r from-amber-800 to-yellow-700 bg-clip-text text-transparent",
                          !isConnected && !hasFailed && !isPending && "bg-gradient-to-r from-gray-800 to-slate-700 bg-clip-text text-transparent"
                        )}>
                          {isPending && "Connecting..."}
                          {hasFailed && "Connection Failed"}
                          {isConnected && "Connected"}
                          {!isConnected && !hasFailed && !isPending && "Not Connected"}
                        </div>
                        
                        {isConnected && userProfileInfo && !isPostingAsOrganization && !isPostingAsFacebookPage && (
                          <div className="flex items-center gap-1 mt-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{userProfileInfo}</span>
                          </div>
                        )}

                        {isConnected && isPostingAsOrganization && selectedOrganization && (
                          <div className="flex items-center gap-1 mt-1">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{selectedOrganization.name}</span>
                          </div>
                        )}

                        {isConnected && isPostingAsFacebookPage && userProfileInfo && (
                          <div className="flex items-center gap-1 mt-1">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{userProfileInfo}</span>
                          </div>
                        )}
                        
                        {isConnected && settings.updated_at && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {formatLastUpdated(settings.updated_at)}
                          </div>
                        )}
                        
                        {hasFailed && settings.error_message && (
                          <div className="text-xs text-red-600 mt-1">
                            {settings.error_message}
                          </div>
                        )}
                        
                        {isPending && (
                          <div className="text-xs text-yellow-600 mt-1">
                            Complete authentication in the popup window
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {!isConnected && !isPending && (
                        <Button 
                          type="button" 
                          variant="default"
                          onClick={handleConnect}
                          disabled={connectionStatus === 'testing'}
                          size="sm"
                          className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 border-0 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          {connectionStatus === 'testing' ? (
                            <RefreshCw className="relative h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <span className="relative mr-2">{icon}</span>
                          )}
                          <span className="relative">
                            {connectionStatus === 'testing' ? 'Testing...' : 'Connect'}
                            {platform.platform_key === 'instagram' && connectionStatus !== 'testing' && (
                              <span className="ml-1">
                                via {instagramAuthType === 'FACEBOOK_LOGIN' ? 'Facebook' : 'Instagram'}
                              </span>
                            )}
                          </span>
                        </Button>
                      )}
                      {isPending && (
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={handleCancelAuth}
                          size="sm"
                          className="group relative overflow-hidden border-red-300 text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-red-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <XCircle className="relative h-4 w-4 group-hover:scale-110 transition-transform duration-200 mr-2" />
                          <span className="relative">Cancel</span>
                        </Button>
                      )}
                      {isConnected && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleConnect}
                            disabled={connectionStatus === 'testing'}
                            size="sm"
                            className="group relative overflow-hidden border-blue-300 text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            {connectionStatus === 'testing' ? (
                              <RefreshCw className="relative h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="relative h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                            )}
                            <span className="sr-only">Test connection</span>
                          </Button>
                          
                          {canViewBrand && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleTestContent}
                                    disabled={testLoading || !formState.autogenerate_enabled}
                                    size="sm"
                                    className="border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {testLoading ? (
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <TestTube className="h-4 w-4" />
                                    )}
                                    <span className="sr-only">Test auto-create</span>
                                  </Button>
                                </TooltipTrigger>
                                {!formState.autogenerate_enabled ? (
                                  <TooltipContent>
                                    <p>Auto-create must be enabled to test content generation</p>
                                  </TooltipContent>
                                ) : formState.autopost_enabled ? (
                                  <TooltipContent>
                                    <p>This will create test content and automatically publish it to {platform.name}</p>
                                  </TooltipContent>
                                ) : (
                                  <TooltipContent>
                                    <p>This will create test content (manual publishing required)</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={handleDisconnect}
                            size="sm"
                            className="group relative overflow-hidden border-red-300 text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-red-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <XCircle className="relative h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                            <span className="sr-only">Disconnect</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="space-y-4">
                {/* Instagram Auth Method Selector (only for Instagram and when not connected) */}
                {platform.platform_key === 'instagram' && !isConnected && (
                  <Card>
                    <CardContent className="p-4">
                    <label className="text-base font-medium mb-2 block">Instagram Login Method</label>
                    <Tabs 
                      defaultValue={instagramAuthType} 
                      className="w-full" 
                      onValueChange={(value) => setInstagramAuthType(value as 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN')}
                    >
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="FACEBOOK_LOGIN" className="flex items-center justify-center gap-2">
                          <Facebook className="h-4 w-4" />
                          <span>Facebook Login</span>
                        </TabsTrigger>
                        <TabsTrigger value="INSTAGRAM_LOGIN" className="flex items-center justify-center gap-2">
                          <Instagram className="h-4 w-4" />
                          <span>Instagram Login</span>
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="FACEBOOK_LOGIN" className="mt-2 text-sm text-muted-foreground">
                        Use this option if your Instagram professional account is linked to a Facebook Page. This provides full access to business features including content publishing, insights, and comment moderation.
                      </TabsContent>
                      <TabsContent value="INSTAGRAM_LOGIN" className="mt-2 text-sm text-muted-foreground">
                        Use this option to connect directly with your Instagram professional account (business or creator). Note that your account must be a professional account to use this option.
                      </TabsContent>
                    </Tabs>
                        <p className="text-xs text-muted-foreground mt-4">
                          Note: To use Instagram's API, you must have a professional account (business or creator). Personal accounts are not supported.
                        </p>
                    </CardContent>
                  </Card>
                )}

                {/* LinkedIn Organization Selector */}
                {platform.platform_key === 'linkedin' && isConnected && (
                  <Card>
                    <CardContent className="p-4">
                    <label className="relative text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-800 group-hover:to-indigo-700 transition-all duration-300">LinkedIn Organization</label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Select the LinkedIn organization to post as (optional)
                    </p>
                    
                    <Select 
                      value={selectedOrganizationId || 'personal'} 
                      onValueChange={handleOrganizationChange}
                      disabled={loadingOrganizations}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            {selectedOrganizationId === 'personal' || !selectedOrganizationId ? (
                              <>
                                <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
                                <span className="truncate">Post as personal account</span>
                              </>
                            ) : selectedOrganization ? (
                              <>
                                <Building className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                <span className="truncate">{selectedOrganization.name}</span>
                              </>
                            ) : (
                              <span>Select an organization</span>
                            )}
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
                            <span className="truncate">Post as personal account</span>
                          </div>
                        </SelectItem>
                        
                        {linkedinOrganizations.length > 0 && <div className="my-1 h-px bg-slate-100"></div>}
                        
                        {linkedinOrganizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="truncate">{org.name || `Organization ${org.id}`}</span>
                            </div>
                          </SelectItem>
                        ))}
                        
                        {linkedinOrganizations.length === 0 && !loadingOrganizations && (
                          <div className="py-2 px-2 text-sm text-slate-500 italic">
                            No organizations found
                          </div>
                        )}
                        
                        {loadingOrganizations && (
                          <div className="py-2 px-2 text-sm text-slate-500 flex items-center gap-2">
                            <RefreshCw className="h-3 w-3 animate-spin flex-shrink-0" />
                            <span className="truncate">Loading organizations...</span>
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {isPostingAsOrganization && selectedOrganization && (
                      <p className="text-sm text-blue-600 mt-2 flex items-center gap-1.5">
                        <Building className="h-3.5 w-3.5" />
                        <span>Posts will be published as {selectedOrganization.name}</span>
                      </p>
                    )}
                    
                    {!isPostingAsOrganization && userProfileInfo && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" />
                          <span>Posts will be published as your personal profile ({userProfileInfo})</span>
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Facebook Page Selector */}
                {platform.platform_key === 'facebook' && isConnected && facebookPages.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                    <label className="relative text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-800 group-hover:to-indigo-700 transition-all duration-300">Facebook Page</label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Select the Facebook page to post as (optional)
                    </p>

                    <Select
                      value={selectedFacebookPageId || 'personal'}
                      onValueChange={handleFacebookPageChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            {selectedFacebookPageId === 'personal' || !selectedFacebookPageId ? (
                              <>
                                <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
                                <span className="truncate">Post as personal profile</span>
                              </>
                            ) : selectedFacebookPage ? (
                              <>
                                <Building className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                <span className="truncate">{selectedFacebookPage.name}</span>
                              </>
                            ) : (
                              <span>Select a page</span>
                            )}
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
                            <span className="truncate">Post as personal profile</span>
                          </div>
                        </SelectItem>

                        {facebookPages.length > 0 && <div className="my-1 h-px bg-slate-100"></div>}

                        {facebookPages.map((page: any) => (
                          <SelectItem key={page.id} value={page.id}>
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="truncate">{page.name || `Page ${page.id}`}</span>
                            </div>
                          </SelectItem>
                        ))}

                        {facebookPages.length === 0 && (
                          <div className="py-2 px-2 text-sm text-slate-500 italic">
                            No pages found
                          </div>
                        )}
                      </SelectContent>
                    </Select>

                    {isPostingAsFacebookPage && selectedFacebookPage && (
                      <p className="text-sm text-blue-600 mt-2 flex items-center gap-1.5">
                        <Building className="h-3.5 w-3.5" />
                        <span>Posts will be published as {selectedFacebookPage.name}</span>
                      </p>
                    )}

                    {!isPostingAsFacebookPage && connectionDetails.user_name && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" />
                          <span>Posts will be published as your personal profile ({connectionDetails.user_name})</span>
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

              </div>
            </div>
            
          )}
          
        </CardContent>
      </Card>

      {/* Keep existing modals but don't render for RSS */}
      {platform?.platform_key !== 'rss' && (
        <>
          {/* Instagram Connection Modal */}
          <Dialog open={showInstagramModal} onOpenChange={setShowInstagramModal}>
            <DialogPortal>
              <DialogOverlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
              <DialogContent className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full sm:max-w-md overflow-hidden bg-gradient-to-br from-white via-pink-50/30 to-white border border-pink-200/50 shadow-2xl backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-50/20 via-transparent to-rose-50/20" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-200/20 to-transparent rounded-full -translate-y-12 translate-x-12" />
          
          <DialogHeader className="relative text-center pb-4">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 shadow-lg shadow-pink-200/50 transition-transform duration-300 hover:scale-110">
              <Instagram className="h-6 w-6 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-pink-700 via-red-700 to-yellow-700 bg-clip-text text-transparent">
              Connect Instagram Business Account
            </DialogTitle>
          </DialogHeader>

          <Alert className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/60 shadow-lg backdrop-blur-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-50/30 to-yellow-50/30" />
            <AlertTriangle className="relative h-5 w-5 text-amber-600" />
            <AlertDescription className="relative text-amber-800">
              <div className="font-bold mb-3 text-amber-900 text-lg">Important note</div>
              <p className="text-sm leading-relaxed">
                Meta only allows Instagram Business Accounts that are connected to a 
                Facebook Business Page to schedule posts. Because of this, we need to 
                authenticate through Facebook.
              </p>
              <p className="text-sm mt-3">
                If you are not sure if you have the right setup, review our guide:
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 h-8 text-xs"
                onClick={() => {
                  // Navigate to help page and show Instagram guide
                  window.open('/help#instagram-guide', '_blank');
                }}
              >
                <BookOpen className="mr-1 h-3 w-3" />
                See Connection Guide
              </Button>
            </AlertDescription>
          </Alert>

          <div className="text-center text-sm text-muted-foreground py-2">
            We're sending you to Facebook (Meta) real quick to sign in and link 
            your account to Rainfactory.
          </div>

          <div className="flex justify-between gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowInstagramModal(false)}
              className="group relative overflow-hidden flex-1 border-gray-300 text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md font-medium"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative">Back</span>
            </Button>
            <Button 
              onClick={handleInstagramAuth}
              className="group relative overflow-hidden flex-1 bg-gradient-to-r from-blue-600 to-blue-700 border-0 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-blue-700 hover:to-blue-800 font-medium"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative flex items-center">
                Go to Facebook Business
                <ExternalLink className="ml-2 h-4 w-4" />
              </span>
            </Button>
          </div>
        </DialogContent>
        </DialogPortal>
      </Dialog>


        </>
      )}
    </div>
  );
};

export default PlatformConnectionCard;